import * as Apollo from '@apollo/react-hooks'
import { ApolloError } from 'apollo-client'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import uuid from 'uuid/v4'

import { LOCIZE_NAMESPACE } from 'main/common'
import { Loader } from 'main/components/common'
import { CREATE_CREDENTIALS_MUTATION } from 'main/components/common/AuthModal/LoginForm/gql'
import { AppContext } from 'main/framework'
import client from 'main/framework/apolloClient'
import useSSOContext from 'main/hooks/useSSOContext'
import { NativeLink } from 'main/ui'
import { login } from 'main/util/auth'
import { UserSignUpSource } from 'main/util/user'

import { i18nKeyPrefix as PRE } from '../../.'
import { initialParams, IParams } from '../../util'
import { CREATE_STAFF_USER_MUTATION } from './SignupForm'
import { BlueSpan, ErrorMessage, GrayText, GroupWrapper, SubmitButton } from './SignupForm.styles'

interface IProps extends RouteComponentProps {
  email: string | null
  onRequestLogin: () => void
  onRequestSignup: () => void
  onRequestLogout: () => void
}

export enum FormType {
  REGISTER,
  LOGIN,
}

function SignupFormSSO({
  email,
  history,
  onRequestLogin,
  onRequestSignup,
  onRequestLogout,
}: IProps) {
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const [formType] = React.useState<FormType>(FormType.REGISTER)
  const [params, setParams] = React.useState<IParams>(initialParams())

  const appContext = React.useContext(AppContext)
  const { ssoContext } = useSSOContext() // this page doesn't set its own nav context but might pass along some
  const [didLogin, setDidLogin] = React.useState<boolean>(false)
  const hasUser = !!appContext?.user?.id

  const [signUpError, setSignUpError] = React.useState<string>('')

  const [signUp, { loading: signupLoading }] = Apollo.useMutation(CREATE_STAFF_USER_MUTATION, {
    onCompleted: () => handleCreateStaffCredentials(),
    onError: error => handleError(error),
  })
  const [createCredentials, { loading: createCredentialsMutationLoading }] = Apollo.useMutation(
    CREATE_CREDENTIALS_MUTATION,
    {
      onCompleted: data => {
        login(client, data.credentials, appContext).then(() => {
          handlePostLogin()
        })
      },
      onError: (error: ApolloError) => handleError(error),
    },
  )

  React.useEffect(() => {
    if (!email) {
      setSignUpError(t(`${PRE}-FORM-INVALID-LINK-ERROR`))
    }
  }, [])

  const isProcessing = appContext?.user?.id || signupLoading || createCredentialsMutationLoading

  async function handleRegister() {
    signUp({
      variables: {
        user: {
          ...params,
          signUpSource: UserSignUpSource.StaffInvitationPage,
          parentTosAgreed: true,
          family: {},
        },
      },
    })
  }

  async function handleCreateStaffCredentials() {
    await createCredentials({
      variables: {
        email: params.email.trim(),
        password: params.password,
      },
    })
  }

  function handlePostLogin() {
    history.push('/staff-accept/download-app')
  }

  React.useEffect(() => {
    // if user already exists and this is a login
    if (hasUser) {
      handlePostLogin()
    }
  }, [hasUser])

  React.useEffect(() => {
    // if signed-up sso user, prefill the form so we can submit it
    if (!hasUser && !didLogin && ssoContext?.signupData) {
      const saved = ssoContext.signupData
      setParams({
        ...initialParams(),
        email: saved.email || '',
        firstName: saved.firstName || '',
        lastName: saved.lastName || '',
        password: uuid(),
      })
      setDidLogin(true)
    }
  }, [ssoContext, didLogin, hasUser])

  React.useEffect(() => {
    if (!hasUser && didLogin && !!params.email) {
      handleRegister()
    }
  }, [hasUser, didLogin, params])

  const maybeRenderErrorSubmessage = (errorMessage: string) => {
    if (errorMessage) {
      switch (errorMessage) {
        case t(`${PRE}-FORM-GENERIC-ERROR`):
          return (
            <>
              <ErrorMessage>{t(`${PRE}-OR-TRY-TXT`)}&nbsp;</ErrorMessage>
              <NativeLink onClick={onRequestLogout}>{t(`${PRE}-LOGGIN-OUT`)}</NativeLink>
            </>
          )
        case t(`${PRE}-FORM-ALREADY-TAKEN-ERROR`):
          return (
            <>
              <ErrorMessage>{t(`${PRE}-TRY-TXT`)}&nbsp;</ErrorMessage>
              <NativeLink onClick={onRequestLogout}>{t(`${PRE}-LOGGIN-IN`)}</NativeLink>
            </>
          )
        case t(`${PRE}-FORM-INVALID-LINK-ERROR`):
          return null
        default:
          return null
      }
    }
  }

  const maybeRenderErrorMessage = (errorMessage: string) => {
    if (errorMessage) {
      return (
        <GroupWrapper>
          <ErrorMessage>{errorMessage}</ErrorMessage>&nbsp;
          {maybeRenderErrorSubmessage(errorMessage)}
        </GroupWrapper>
      )
    }
    return null
  }

  const renderLoginForm = () => {
    return (
      <>
        <SubmitButton
          full
          kind="regular"
          onClick={() => {
            onRequestLogin()
          }}
        >
          {t(`${PRE}-LOGIN-BTN`)}
        </SubmitButton>
        <GrayText>
          {t(`${PRE}-DO-NOT-HAVE-ACCOUNT-TXT`)}{' '}
          <BlueSpan
            onClick={() => {
              onRequestSignup()
            }}
          >
            {t(`${PRE}-CREATE-ACCOUNT-TXT`)}
          </BlueSpan>{' '}
          {t(`${PRE}-INSTEAD-TXT`)}
        </GrayText>
      </>
    )
  }

  const renderRegisterForm = () => {
    return (
      <>
        <SubmitButton
          full
          kind="regular"
          onClick={() => {
            onRequestSignup()
          }}
        >
          {t(`${PRE}-GET-STARTED-BTN`)}
        </SubmitButton>
        <GrayText>
          {t(`${PRE}-ALREADY-HAVE-ACCOUNT`)}{' '}
          <BlueSpan
            onClick={() => {
              onRequestLogin()
            }}
          >
            {t(`${PRE}-LOGIN-BTN`)}
          </BlueSpan>{' '}
          {t(`${PRE}-INSTEAD-TXT`)}
        </GrayText>
      </>
    )
  }

  const maybeRenderForm = () => {
    if (isProcessing) {
      return <Loader />
    } else if (formType === FormType.LOGIN) {
      return renderLoginForm()
    } else {
      return renderRegisterForm()
    }
  }

  return (
    <>
      {maybeRenderErrorMessage(signUpError)}
      {maybeRenderForm()}
    </>
  )

  function handleError(error: ApolloError): void {
    const graphQLError = error.graphQLErrors[0]
    const errorToLocizeKeys = new Map([
      ['email has already been taken', `${PRE}-FORM-ALREADY-TAKEN-ERROR`],
    ])

    if (!errorToLocizeKeys.has(graphQLError.message)) {
      setSignUpError(t(`${PRE}-FORM-GENERIC-ERROR`))
    } else {
      setSignUpError(t(errorToLocizeKeys.get(graphQLError.message) || ''))
    }
  }
}

export default withRouter(SignupFormSSO)
